import axios from "axios";
import apiClient from "./validTokenMiddleware";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getCitizenValue = () => {
  const selectedCitizen = JSON.parse(localStorage.getItem("selectedCitizen"));
  // console.log("SELECTED CITIZEN IN API CALLS", selectedCitizen);
  return selectedCitizen ? selectedCitizen.value : null;
};

// Function that gets all localStorage values
export const getLocalStorageValues = () => ({
  clinician_id: localStorage.getItem("clinician_id"),
  clinician_name: localStorage.getItem("clinician_name"),
  token: localStorage.getItem("authToken"),
  userIdSelected: localStorage.getItem("userSelectedId"),
  // 'clinician' | 'service' | 'admin'. Drives admin-only UI and swaps a
  // few API calls to their /all variants. Missing for sessions started
  // before the role rollout — treated as 'clinician' downstream.
  role: localStorage.getItem("role"),
});

// True when the logged-in user is an admin. Centralized so per-call sites
// don't reach into localStorage with their own string comparisons.
export const isAdmin = () => localStorage.getItem("role") === "admin";
export const clinicianRegistration = async (info) => {
  try {
    const payload = {
      username: info.userName,
      password: info.password,
      email: info.email,
      full_name: info.fullName,
      location: info.location,
      organization: info.organization,
      specilization: info.specilization,
      telephone: info.telephone,
    };
    //console.log("Payload of clinician, registration", payload);
    const response = await axios.post(`${API_BASE_URL}/clinicians/`, payload);
    return response.data;
  } catch (error) {
    console.error("Error on user registration:", error);
    throw error;
  }
};

export const clinicianLogin = async (credentials) => {
  try {
    const payload = {
      username: credentials.username,
      password: credentials.password,
    };

    const response = await apiClient.post(
      `${API_BASE_URL}/clinicians/login/`,
      payload
    );

    const token = response.data.access_token;
    localStorage.setItem("authToken", token);

    localStorage.removeItem("sessionExpiredMessage");
    const clinicianUserName = payload.username;
    localStorage.setItem("clinician_name", clinicianUserName);

    const clinician_id = response.data.clinician_id;
    localStorage.setItem("clinician_id", clinician_id);

    // Default to "clinician" so sessions started before the role
    // rollout (response.data.role would be undefined) still get a
    // sensible value in localStorage.
    localStorage.setItem("role", response.data.role || "clinician");

    // console.log(response.data);
    return response.data;
  } catch (error) {
    console.error("Error on user login:", error);
    throw error;
  }
};

export const clinicianDetails = async () => {
  try {
    const { clinician_id } = getLocalStorageValues();

    const response = await apiClient.get(
      `${API_BASE_URL}/clinicians/${clinician_id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error when fetching clinician details.");
  }
};

export const getBasicUserInfo = async (citizenValue) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/users/subject/${citizenValue}`
    );
    // console.log("Basic info fetched successfully: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error when fetching basic details", error);
  }
};

export const putClinicianDetails = async (info) => {
  const { token, clinicianUserName, clinician_id } = getLocalStorageValues();
  // Filter out empty fields
  const filterEmptyFields = (data) => {
    return Object.keys(data).reduce((acc, key) => {
      if (data[key]) {
        acc[key] = data[key];
      }
      return acc;
    }, {});
  };

  try {
    const payload = {
      username: clinicianUserName,
      password: info.password,
      email: info.email,
      full_name: info.fullName,
      location: info.location,
      organization: info.organization,
      specilization: info.specilization,
      telephone: info.telephone,
    };
    // console.log("PAYLOAD OF PUT DETAILS CLINICIAN", payload);
    const response = await apiClient.put(
      `${API_BASE_URL}/clinicians/${clinician_id}`,
      // `${API_BASE_URL}/clinicians/${clinician_id}/`,

      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    // console.log("User updated successfully:", response.data);
  } catch (error) {
    console.error("Error when changing clinician details", error);
    throw error;
  }
};

export const userByClinician = async () => {
  try {
    // Admins see every patient across every clinician via /users/all.
    // Clinicians keep the existing per-clinician scope.
    const url = isAdmin()
      ? `${API_BASE_URL}/users/all`
      : `${API_BASE_URL}/users/clinician/${getLocalStorageValues().clinician_id}`;
    const response = await apiClient.get(url);
    return response.data || [];
  } catch (error) {
    console.error(
      "Error when fetching users that corresponds with this clinician.",
      error
    );
    throw error;
  }
};

export const getCitizensOverview = async () => {
  try {
    // Same admin/clinician split as userByClinician — overview shape is
    // identical, the backend just skips the clinician filter for admins.
    const url = isAdmin()
      ? `${API_BASE_URL}/users/all/overview`
      : `${API_BASE_URL}/users/clinician/${getLocalStorageValues().clinician_id}/overview`;
    const response = await apiClient.get(url);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching citizens overview:", error);
    throw error;
  }
};

export const userRisk = async (userID) => {
  try {
    //console.log("Trying to fetch user's risk");
    const response = await apiClient.get(
      `${API_BASE_URL}/stratification/user/${userID}`
    );
    // console.log("Data response of userRisk", response.data);
    return response.data || [];
  } catch (error) {
    console.error("Error when fetching user risk", error);
  }
};

export const getRules = async (ruleId) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/rules/stratification/${ruleId}`
    );
    // console.log("Response of rules", response.data);
    return response.data || [];
  } catch (error) {
    console.error("Error when getting the rules", error);
  }
};

export const patientRecommendations = async (userID) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/depo/recommendations/user/${userID}`
    );
    // console.log("Patient recommendations", response.data);
    return response.data;
  } catch (error) {
    console.error("Error when fetching patient's recommendations.");
  }
};

export const getRecomFeedbackByUser = async (userID) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/depo/recom-feedback/user/${userID}`
    );
    return response.data || [];
  } catch (error) {
    // If there's no feedback yet, the endpoint returns an empty list; a
    // network / 5xx is worth surfacing, but we degrade gracefully to [] so
    // the recommendations table still renders without feedback indicators.
    console.error("Error when fetching recommendation feedback.", error);
    return [];
  }
};

export const postRecommendation = async (newRecommendation) => {
  try {
    const response = await apiClient.post(
      `${API_BASE_URL}/depo/recommendations/`,
      {
        user_uuid: newRecommendation.user_uuid,
        recommendation: newRecommendation.recommendation,
        status: newRecommendation.status,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error when posting clinician's recommendations.", error);
    throw error;
  }
};

export const putRecommendation = async (info) => {
  const { userIdSelected, token } = getLocalStorageValues();
  // console.log(
  //   "User selected: ",
  //   `${userIdSelected}`,
  //   info,
  //   "with token:",
  //   token
  // );

  // Filter out empty fields
  const filterEmptyFields = (data) => {
    return Object.keys(data).reduce((acc, key) => {
      if (data[key]) {
        acc[key] = data[key];
      }
      return acc;
    }, {});
  };

  try {
    const payload = filterEmptyFields({
      id: info.id,
      user_uuid: userIdSelected,
      recommendation: info.recommendation,
      status: info.status,
      notes: info.notes,
    });
    // console.log("PAYLOAD OF PUT RECOMMENDATIONS ", payload);
    const response = await apiClient.put(
      `${API_BASE_URL}/depo/recommendations/`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    // console.log("Recommendation updated successfully:", response);
  } catch (error) {
    console.error("Error when changing recommendation details", error);
    throw error;
  }
};

export const biasAssessment = async () => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/appo`);

    // console.log("Data response of biasAssessment", response.data);
    return response.data || [];
  } catch (error) {
    console.error("Error when fetching bias assessment", error);
  }
};

// Sleep data
export const getAllSleepData = async () => {
  try {
    const citizenValue = getCitizenValue();
    const response = await apiClient.get(
      `${API_BASE_URL}/sleep/subject/${citizenValue}`
    );

    // console.log("Data for sleep of ", citizenValue, ": ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error when fetching all sleep data: ", error);
    throw error;
  }
};

export const getSleepDataBuSubjectID = async (subject_id) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/sleep/subject/${subject_id}`
    );
    return response.data;
  } catch (error) {
    // console.log(
    //   `Error when fetching sleep data for subject id: ${subject_id}`,
    //   error
    // );
  }
};

export const getAllExerciseData = async () => {
  try {
    const citizenValue = getCitizenValue();
    const response = await apiClient.get(
      `${API_BASE_URL}/exercise/subject/${citizenValue}`
    );

    // console.log("Exercise data for ", citizenValue, ": ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error when fetching all exercise data: ", error);
    throw error;
  }
};

export const getExerciseDataBySubjectID = async (subject_id) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/exercise/subject/${subject_id}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error when fetching exercise data of ${subject_id}:`, error);
  }
};

export const getAllFitnessData = async () => {
  try {
    const citizenValue = getCitizenValue();
    // console.log("userSelected", citizenValue);
    const response = await apiClient.get(
      `${API_BASE_URL}/fitness/subject/${citizenValue}`
    );
    // console.log("Fitness data for ", citizenValue, ": ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error when fetching fitness data", error);
    throw error;
  }
};
export const getFitnessDataBySubjectID = async (subject_id) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/fitness/subject/${subject_id}`
    );
    return response.data;
  } catch (error) {
    // console.log(
    //   `Error when fetching fitness data for subject id: ${subject_id}`,
    //   error
    // );
  }
};
export const getAllConsumptionData = async () => {
  try {
    const citizenValue = getCitizenValue();
    const response = await apiClient.get(
      `${API_BASE_URL}/consumption/subject/${citizenValue}`
    );

    // console.log("Consumption data for ", citizenValue, ": ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error when fetching consumption data: ", error);
  }
};

export const getConsumptionDataBySubjectID = async (subject_id) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/consumption/subject/${subject_id}`
    );
    return response.data;
  } catch (error) {
    // console.log(
    //   `Error when fetching consumption data for subject id: ${subject_id}`,
    //   error
    // );
  }
};
export const getAllHeartRateData = async () => {
  try {
    const citizenValue = getCitizenValue();

    const response = await apiClient.get(
      `${API_BASE_URL}/heart-rate/subject/${citizenValue}`
    );

    // console.log("Heart rate data for ", citizenValue, ": ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error when fetching heart rate data: ", error);
  }
};

export const getHeartRateBySubjectID = async (subject_id) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/heart-rate/subject/${subject_id}`
    );
    return response.data;
  } catch (error) {
    // console.log(
    //   `Error when fetching heart rate data for subject id: ${subject_id}`,
    //   error
    // );
  }
};
export const getQuestionnaireBySubjectID = async (subject_id) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/questionnaire/subject/${subject_id}`
    );
    // console.log("response of questionnaire ", response.data);

    return response.data;
  } catch (error) {
    console.error(
      "Error when fetching questionnaires of ",
      subject_id,
      "with error ",
      error
    );
  }
};

export const getQueestionnaireByDateRange = async (
  date1,
  date2,
  subject_id
) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/questionnaire/dates-and-subject/`,
      {
        params: {
          start_date: date1,
          end_date: date2,
          subject_id: subject_id,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error when fetching questionnaires by date range ",
      date1,
      "-",
      date2,
      " for",
      subject_id,
      " with error:",
      error
    );
  }
};

export const getWeightBySubjectID = async (subject_id) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/weight/subject/${subject_id}`
    );
    // console.log("Response of weight: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error when fetching weight: ", error);
  }
};

export const getBasicUserInfoBySubjectID = async (subject_id) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}/basic-user-info/${subject_id}`
    );
    // console.log(
    //   "Sucessfully fetched basic user info by subject ID: ",
    //   response.data
    // );
    return response.data;
  } catch (error) {
    console.error("Error when fetching basic user info by subject ID: ", error);
  }
};

export const getStatisticsForAllSubjects = async (clinicianId, filters = {}) => {
  try {
    // Admins omit clinician_id so the backend aggregates across every
    // clinician's cohort. Clinicians keep the existing per-clinician scope.
    const params = isAdmin() ? {} : { clinician_id: clinicianId };

    // Add optional filters if provided
    if (filters.sex) params.sex = filters.sex;
    if (filters.age_min !== undefined && filters.age_min !== null) params.age_min = filters.age_min;
    if (filters.age_max !== undefined && filters.age_max !== null) params.age_max = filters.age_max;
    if (filters.country) params.country = filters.country;
    if (filters.education) params.education = filters.education;
    if (filters.region) params.region = filters.region;
    if (filters.risk_level !== undefined && filters.risk_level !== null) params.risk_level = filters.risk_level;

    const response = await apiClient.get(`${API_BASE_URL}/statistics`, { params });
    return response.data;
  } catch (error) {
    console.error("Error when fetching statistics for all subjects: ", error);
  }
};

export const getStatisticsFilterOptions = async (clinicianId) => {
  try {
    const params = isAdmin() ? {} : { clinician_id: clinicianId };
    const response = await apiClient.get(`${API_BASE_URL}/statistics/filter-options`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error when fetching statistics filter options: ", error);
  }
};

// AI Assistant Chat
export const sendChatMessage = async (message, subjectId, conversationId = null) => {
  try {
    const payload = {
      message,
      subject_id: subjectId,
    };
    if (conversationId) {
      payload.conversation_id = conversationId;
    }
    const response = await apiClient.post(
      `${API_BASE_URL}/ai-assistant/chat`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error sending chat message:", error);
    throw error;
  }
};

export const clearConversation = async (conversationId) => {
  try {
    await apiClient.delete(`${API_BASE_URL}/ai-assistant/chat/${conversationId}`);
  } catch (error) {
    console.error("Error clearing conversation:", error);
  }
};

// Notifications API calls
export const getNotifications = async (category = null, unreadOnly = false, limit = 50, offset = 0) => {
  try {
    const { clinician_id } = getLocalStorageValues();
    const params = { limit, offset };
    if (category) params.category = category;
    if (unreadOnly) params.unread_only = unreadOnly;

    const response = await apiClient.get(
      `${API_BASE_URL}/notifications/clinician/${clinician_id}`,
      { params }
    );
    return response.data || [];
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return [];
  }
};

export const getNotificationCount = async () => {
  try {
    const { clinician_id } = getLocalStorageValues();
    const response = await apiClient.get(
      `${API_BASE_URL}/notifications/count/${clinician_id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching notification count:", error);
    return { unread_count: 0, total_count: 0 };
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await apiClient.put(
      `${API_BASE_URL}/notifications/${notificationId}/read`
    );
    return response.data;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const { clinician_id } = getLocalStorageValues();
    const response = await apiClient.put(
      `${API_BASE_URL}/notifications/clinician/${clinician_id}/read-all`
    );
    return response.data;
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    throw error;
  }
};

export const deleteNotification = async (notificationId) => {
  try {
    await apiClient.delete(`${API_BASE_URL}/notifications/${notificationId}`);
  } catch (error) {
    console.error("Error deleting notification:", error);
    throw error;
  }
};

export const getNotificationPreferences = async () => {
  try {
    const { clinician_id } = getLocalStorageValues();
    const response = await apiClient.get(
      `${API_BASE_URL}/notifications/preferences/${clinician_id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching notification preferences:", error);
    return null;
  }
};

export const updateNotificationPreferences = async (preferences) => {
  try {
    const { clinician_id } = getLocalStorageValues();
    const response = await apiClient.put(
      `${API_BASE_URL}/notifications/preferences/${clinician_id}`,
      preferences
    );
    return response.data;
  } catch (error) {
    console.error("Error updating notification preferences:", error);
    throw error;
  }
};

// Export API calls
export const getBulkExportData = async (filters = {}) => {
  try {
    const { clinician_id } = getLocalStorageValues();
    const params = { clinician_id, ...filters };

    // Remove undefined/null values from params
    Object.keys(params).forEach(
      (key) => (params[key] === undefined || params[key] === null) && delete params[key]
    );

    const response = await apiClient.get(`${API_BASE_URL}/export/bulk`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching bulk export data:", error);
    throw error;
  }
};

export const getUserReportData = async (userId) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/export/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching user report data:", error);
    throw error;
  }
};
